import { ArrowUpRight, Activity, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  canAskQuestion: boolean;
  hasInteracted: boolean;
  enableBellwether: boolean;
  onBellwetherChange: (enabled: boolean) => void;
  enableMacro: boolean;
  onMacroChange: (enabled: boolean) => void;
}

export const ChatInput = ({ 
  value, 
  onChange, 
  onSubmit, 
  isLoading, 
  canAskQuestion,
  hasInteracted,
  enableBellwether,
  onBellwetherChange,
  enableMacro,
  onMacroChange
}: ChatInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 pb-4 pt-2 bg-gradient-to-t from-[#0c1221] to-transparent">
      <div className={cn(
        "flex flex-col w-full max-w-4xl mx-auto",
        isMobile ? "px-3" : "px-4 md:px-6"
      )}>
        {/* Main input form */}
        <form 
          className="w-full" 
          onSubmit={onSubmit}
        >
          <div className="relative w-full bg-[#111827] rounded-xl shadow-lg flex items-center pr-2.5">
            {/* Left icons */}
            <div className={cn(
              "flex items-center pl-4",
              isMobile ? "space-x-2" : "space-x-2.5"
            )}>
              <button
                type="button"
                onClick={() => onBellwetherChange(!enableBellwether)}
                disabled={isLoading || !canAskQuestion}
                className={cn(
                  "flex items-center justify-center",
                  "transition-colors duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  enableBellwether ? "text-blue-400" : "text-gray-400 hover:text-gray-300",
                  isMobile ? "h-4 w-4" : "h-4 w-4"
                )}
                aria-label="Toggle Market Health"
              >
                <Activity className="h-4 w-4" />
              </button>
              
              <button
                type="button"
                onClick={() => onMacroChange(!enableMacro)}
                disabled={isLoading || !canAskQuestion}
                className={cn(
                  "flex items-center justify-center",
                  "transition-colors duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  enableMacro ? "text-blue-400" : "text-gray-400 hover:text-gray-300",
                  isMobile ? "h-4 w-4" : "h-4 w-4"
                )}
                aria-label="Toggle Macro Insights"
              >
                <Calendar className="h-4 w-4" />
              </button>
            </div>
            
            {/* Input field */}
            <input
              ref={inputRef}
              type="text"
              placeholder="Message Strykr..."
              className={cn(
                "flex-1 bg-transparent border-none text-white",
                "focus:outline-none focus:ring-0",
                "placeholder:text-gray-500",
                !canAskQuestion && "opacity-50",
                isMobile 
                  ? "py-3 px-3 h-11 finance-text-base" 
                  : "py-3 px-3 h-11 text-base"
              )}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading || !canAskQuestion}
              autoFocus
            />
            
            {/* Send button */}
            <button
              type="submit"
              disabled={isLoading || !canAskQuestion || !value.trim()}
              className={cn(
                "h-7 w-7 rounded-full flex items-center justify-center",
                "bg-blue-600 hover:bg-blue-500",
                "transition-colors duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                !value.trim() && "opacity-50"
              )}
              aria-label="Send message"
            >
              <ArrowUpRight className="h-3.5 w-3.5 text-white" />
            </button>
          </div>
        </form>
        
        {/* Optional disclaimer text */}
        <div className={cn(
          "mt-2 text-center text-white/40",
          isMobile ? "finance-text-xs" : "text-xs"
        )}>
          Strykr.ai may produce inaccurate information. Verify important information.
        </div>
      </div>
    </div>
  );
};
